"use client";

import { useState, type FC, type ReactNode } from "react";
import * as RadixPopover from "@radix-ui/react-popover";
import type { TaxonomyOption } from "cmw-client";
import { useDrawer } from "@/components/drawer";
import { useIsMobile } from "@/hooks/use-mobile";
import { AllFiltersPanel } from "./all-filters-panel";
import type { DirectoryFilters } from "./filters";

export type AllFiltersProps = {
  filters: DirectoryFilters;
  onChange: (filters: DirectoryFilters) => void;
  taxonomy?: Record<string, TaxonomyOption[]>;
};

/* Opens the panel as a popover from `md` up and as the drawer sheet below it. */
export const AllFiltersTrigger: FC<AllFiltersProps & { children: ReactNode }> = ({
  filters,
  onChange,
  taxonomy,
  children,
}) => {
  const isMobile = useIsMobile();
  const { openDrawer, closeDrawer } = useDrawer();
  const [isOpen, setIsOpen] = useState(false);

  const openSheet = () =>
    openDrawer(
      <AllFiltersPanel
        filters={filters}
        taxonomy={taxonomy}
        onApply={(next) => {
          onChange(next);
          closeDrawer();
        }}
      />,
      { title: "All Filters", description: "Narrow the directory by profession, session type and more." },
    );

  return (
    <RadixPopover.Root open={isOpen} onOpenChange={(next) => (isMobile && next ? openSheet() : setIsOpen(next))}>
      <RadixPopover.Trigger asChild>{children}</RadixPopover.Trigger>
      <RadixPopover.Portal>
        <RadixPopover.Content
          align="end"
          side="bottom"
          sideOffset={8}
          collisionPadding={16}
          className="z-40 flex max-h-[min(70dvh,720px)] w-560 max-w-[calc(100vw-32px)] flex-col rounded-large border border-neutral-light-grey bg-neutral-white p-24 shadow-lg data-[state=closed]:animate-[dropdown-out_150ms_ease-in] data-[state=open]:animate-[dropdown-in_150ms_ease-out]"
        >
          <AllFiltersPanel
            heading
            filters={filters}
            taxonomy={taxonomy}
            onApply={(next) => {
              onChange(next);
              setIsOpen(false);
            }}
          />
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  );
};
